import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Bell,
  Shield,
  LogOut,
  ChevronRight,
  Edit2,
  Camera,
  Check,
} from 'lucide-react';
import {
  Card,
  Button,
  ShimmerButton,
  Input,
  Switch,
  Avatar,
  ResponsiveDialog,
  useToast,
  useViewport,
  cn,
  formatNaira,
  staggerChildren,
  fadeInUp,
} from '@raffle/ui';
import { useAuth } from '../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';

export const ProfilePage: React.FC = () => {
  const { isDesktop } = useViewport();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    fullName,
    email: user?.email || '',
    phone: user?.phone || '',
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    drawReminders: true,
    promotions: false,
  });

  const handleSaveProfile = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSaving(false);
    setShowEditModal(false);
    toast.success('Profile Updated', 'Your profile has been updated successfully');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.info('Signed Out', 'You have been signed out successfully');
  };

  // Mock stats
  const stats = {
    totalTickets: 24,
    activaRaffles: 3,
    totalWon: 0,
    totalSpent: 145000,
  };

  return (
    <div className="page-container">
      <section className="content-wrapper py-8">
        {/* Profile Header */}
        <motion.div
          className="relative mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="overflow-hidden">
            {/* Banner */}
            <div className="h-24 md:h-32 bg-gradient-to-r from-gold-600/20 via-gold-500/10 to-gold-600/20" />

            {/* Profile Info */}
            <div className="relative px-6 pb-6">
              {/* Avatar */}
              <div className="absolute -top-12 left-6">
                <div className="relative">
                  <Avatar
                    alt={fullName || 'User'}
                    fallback={fullName || 'User'}
                    src={user?.avatarUrl}
                    size="xl"
                    className="ring-4 ring-surface-1"
                  />
                  <button className="absolute bottom-0 right-0 p-2 rounded-full bg-gold-500 text-black hover:bg-gold-400 transition-colors">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-2 mb-8">
                <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>

              {/* Info */}
              <div>
                <h1 className="font-display text-2xl font-bold text-text-primary mb-1">
                  {fullName || 'Guest User'}
                </h1>
                <p className="text-text-muted">Member since January 2024</p>
              </div>

              {/* Quick Stats */}
              <div className={cn(
                'grid gap-4 mt-6 pt-6 border-t border-white/5',
                isDesktop ? 'grid-cols-4' : 'grid-cols-2'
              )}>
                {[
                  { label: 'Total Tickets', value: stats.totalTickets },
                  { label: 'Active Raffles', value: stats.activaRaffles },
                  { label: 'Wins', value: stats.totalWon },
                  { label: 'Total Spent', value: formatNaira(stats.totalSpent) },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="font-mono text-xl md:text-2xl font-bold text-text-primary tabular-nums">
                      {stat.value}
                    </p>
                    <p className="text-xs text-text-muted">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Settings Grid */}
        <motion.div
          className={cn('grid gap-6', isDesktop ? 'grid-cols-2' : 'grid-cols-1')}
          variants={staggerChildren}
          initial="hidden"
          animate="show"
        >
          {/* Account Info */}
          <motion.div variants={fadeInUp}>
            <Card>
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-gold-500" />
                  Account Information
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-text-muted" />
                      <div>
                        <p className="text-sm text-text-muted">Email</p>
                        <p className="text-text-primary">{user?.email || 'Not set'}</p>
                      </div>
                    </div>
                    <Check className="w-4 h-4 text-green-400" />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-text-muted" />
                      <div>
                        <p className="text-sm text-text-muted">Phone</p>
                        <p className="text-text-primary">{user?.phone || 'Not set'}</p>
                      </div>
                    </div>
                    {user?.phone && <Check className="w-4 h-4 text-green-400" />}
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-text-muted" />
                      <div>
                        <p className="text-sm text-text-muted">Location</p>
                        <p className="text-text-primary">Lagos, Nigeria</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Notifications */}
          <motion.div variants={fadeInUp}>
            <Card>
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-gold-500" />
                  Notifications
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-text-primary">Email Notifications</p>
                      <p className="text-sm text-text-muted">Receive updates via email</p>
                    </div>
                    <Switch
                      checked={notifications.email}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, email: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-text-primary">Push Notifications</p>
                      <p className="text-sm text-text-muted">Get instant updates</p>
                    </div>
                    <Switch
                      checked={notifications.push}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, push: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-text-primary">Draw Reminders</p>
                      <p className="text-sm text-text-muted">Notify before draws</p>
                    </div>
                    <Switch
                      checked={notifications.drawReminders}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, drawReminders: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-text-primary">Promotions</p>
                      <p className="text-sm text-text-muted">Special offers & deals</p>
                    </div>
                    <Switch
                      checked={notifications.promotions}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, promotions: checked }))}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Security */}
          <motion.div variants={fadeInUp}>
            <Card>
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gold-500" />
                  Security
                </h2>

                <div className="space-y-2">
                  <button className="w-full flex items-center justify-between p-4 rounded-vault bg-surface-2/50 hover:bg-surface-2 border border-white/5 transition-colors">
                    <div className="text-left">
                      <p className="text-text-primary">Change Password</p>
                      <p className="text-sm text-text-muted">Update your password</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  </button>

                  <button className="w-full flex items-center justify-between p-4 rounded-vault bg-surface-2/50 hover:bg-surface-2 border border-white/5 transition-colors">
                    <div className="text-left">
                      <p className="text-text-primary">Two-Factor Authentication</p>
                      <p className="text-sm text-text-muted">Add extra security</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  </button>

                  <button className="w-full flex items-center justify-between p-4 rounded-vault bg-surface-2/50 hover:bg-surface-2 border border-white/5 transition-colors">
                    <div className="text-left">
                      <p className="text-text-primary">Login History</p>
                      <p className="text-sm text-text-muted">View recent activity</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Danger Zone */}
          <motion.div variants={fadeInUp}>
            <Card className="border-vault-error/20">
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <LogOut className="w-5 h-5 text-vault-error" />
                  Account Actions
                </h2>

                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setShowLogoutConfirm(true)}
                    className="justify-start"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>

                  <button className="w-full flex items-center gap-2 p-3 rounded-vault text-vault-error/70 hover:text-vault-error hover:bg-vault-error/10 transition-colors text-sm">
                    Delete Account
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </section>

      {/* Edit Profile Modal */}
      <ResponsiveDialog
        isOpen={showEditModal}
        onClose={() => !isSaving && setShowEditModal(false)}
        title="Edit Profile"
      >
        <div className="p-6 space-y-6">
          <Input
            label="Full Name"
            value={editForm.fullName}
            onChange={(e) => setEditForm((prev) => ({ ...prev, fullName: e.target.value }))}
            placeholder="Enter your full name"
          />

          <Input
            label="Email"
            type="email"
            value={editForm.email}
            onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="Enter your email"
          />

          <Input
            label="Phone Number"
            type="tel"
            value={editForm.phone}
            onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
            placeholder="Enter your phone number"
          />

          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowEditModal(false)} disabled={isSaving}>
              Cancel
            </Button>
            <ShimmerButton fullWidth onClick={handleSaveProfile} loading={isSaving}>
              Save Changes
            </ShimmerButton>
          </div>
        </div>
      </ResponsiveDialog>

      {/* Logout Confirmation */}
      <ResponsiveDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="Sign Out"
        modalSize="sm"
      >
        <div className="p-6 space-y-6">
          <p className="text-text-secondary text-center">
            Are you sure you want to sign out of your account?
          </p>

          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowLogoutConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" fullWidth onClick={handleLogout}>
              Sign Out
            </Button>
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
